import React, { useCallback, useEffect, useState } from 'react';
import PolarisAdminSelect from './PolarisAdminSelect';
import {
    createPolarisSport,
    fetchPolarisSportCatalog,
    SportCategoryAdminItem,
    updatePolarisSport,
} from '../services/polarisService';
import PolarisConfirmModal from './PolarisConfirmModal';
import '../styles/polaris-admin.css';

const SPORT_CATEGORIES = [
    'NCAA Championship Sports',
    'NCAA Emerging Sports',
    'Additional Collegiate Athletic Opportunities',
] as const;

type PendingCategoryMove = {
    sportId: number;
    sportName: string;
    fromCategory: string;
    toCategory: string;
};

export default function SportsManagementPanel() {
    const [categories, setCategories] = useState<SportCategoryAdminItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string>(SPORT_CATEGORIES[0]);
    const [newSportName, setNewSportName] = useState('');
    const [saving, setSaving] = useState(false);
    const [actionSportId, setActionSportId] = useState<number | null>(null);
    const [pendingMove, setPendingMove] = useState<PendingCategoryMove | null>(null);
    const [moving, setMoving] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const data = await fetchPolarisSportCatalog();
            setCategories(data);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to load sports.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleCreateSport = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedCategory || !newSportName.trim()) {
            setError('Choose a category and enter a sport name.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await createPolarisSport(selectedCategory, newSportName.trim());
            setNewSportName('');
            await load();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to create sport.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (sportId: number, active: boolean) => {
        setActionSportId(sportId);
        setError(null);
        try {
            await updatePolarisSport(sportId, { active });
            await load();
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : 'Failed to update sport.');
        } finally {
            setActionSportId(null);
        }
    };

    const handleCategorySelect = (
        sportId: number,
        sportName: string,
        fromCategory: string,
        toCategory: string,
    ) => {
        if (!toCategory || toCategory === fromCategory) {
            return;
        }
        setModalError(null);
        setPendingMove({ sportId, sportName, fromCategory, toCategory });
    };

    const handleCloseMoveModal = () => {
        if (moving) return;
        setPendingMove(null);
        setModalError(null);
    };

    const handleConfirmMove = async () => {
        if (!pendingMove) return;

        setMoving(true);
        setModalError(null);
        setError(null);
        try {
            await updatePolarisSport(pendingMove.sportId, { category: pendingMove.toCategory });
            setPendingMove(null);
            await load();
        } catch (e: unknown) {
            setModalError(e instanceof Error ? e.message : 'Failed to move sport.');
        } finally {
            setMoving(false);
        }
    };

    return (
        <div data-testid="polaris-sports-panel">
            <p style={{ color: '#6b7280', margin: '0 0 1.5rem' }}>
                Manage athlete sports. Hiding a sport removes it from athlete pickers but keeps existing profile
                selections intact. Moving a sport between categories only changes how it is grouped in pickers —
                athlete profile links and sport names are preserved.
            </p>

            {error && <div className="pa-error">{error}</div>}
            {loading && <div className="pa-loading">Loading sports…</div>}

            {!loading && (
                <>
                    <div className="pa-metric-card pa-metric-card-full" style={{ marginBottom: 24 }}>
                        <div className="pa-metric-card-title">Add Sport</div>
                        <form
                            style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}
                            onSubmit={handleCreateSport}
                        >
                            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                <span>Category</span>
                                <PolarisAdminSelect
                                    size="compact"
                                    value={selectedCategory}
                                    options={SPORT_CATEGORIES.map((category) => ({
                                        value: category,
                                        label: category,
                                    }))}
                                    onChange={setSelectedCategory}
                                    data-testid="polaris-reference-data-sport-category"
                                />
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                <span>Sport name</span>
                                <input
                                    className="pa-filter-input"
                                    value={newSportName}
                                    onChange={(e) => setNewSportName(e.target.value)}
                                    placeholder="e.g. Esports"
                                    data-testid="polaris-reference-data-sport-name"
                                />
                            </label>
                            <button
                                type="submit"
                                className="pa-page-btn"
                                disabled={saving}
                                data-testid="polaris-reference-data-add-sport"
                            >
                                {saving ? 'Adding…' : 'Add Sport'}
                            </button>
                        </form>
                    </div>

                    {categories.map((category) => (
                        <div key={category.name} className="pa-metric-card pa-metric-card-full" style={{ marginBottom: 24 }}>
                            <div className="pa-metric-card-title">{category.name}</div>
                            <table className="pa-table">
                                <thead>
                                    <tr>
                                        <th>Sport</th>
                                        <th>Category</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {category.sports.length === 0 && (
                                        <tr>
                                            <td colSpan={4}>No sports in this category yet.</td>
                                        </tr>
                                    )}
                                    {category.sports.map((sport) => (
                                        <tr key={sport.id} data-testid={`polaris-sport-row-${sport.id}`}>
                                            <td>{sport.name}</td>
                                            <td>
                                                <PolarisAdminSelect
                                                    size="compact"
                                                    value={category.name}
                                                    options={SPORT_CATEGORIES.map((option) => ({
                                                        value: option,
                                                        label: option,
                                                    }))}
                                                    disabled={actionSportId === sport.id || moving}
                                                    onChange={(next) =>
                                                        handleCategorySelect(
                                                            sport.id,
                                                            sport.name,
                                                            category.name,
                                                            next,
                                                        )
                                                    }
                                                    data-testid={`polaris-sport-category-${sport.id}`}
                                                />
                                            </td>
                                            <td>
                                                <span className={`pa-badge ${sport.active ? 'pa-badge-active' : 'pa-badge-inactive'}`}>
                                                    {sport.active ? 'Visible' : 'Hidden'}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="pa-page-btn"
                                                    disabled={actionSportId === sport.id || moving}
                                                    onClick={() => handleToggleActive(sport.id, !sport.active)}
                                                    data-testid={`polaris-sport-toggle-${sport.id}`}
                                                >
                                                    {actionSportId === sport.id
                                                        ? 'Saving…'
                                                        : sport.active
                                                            ? 'Hide'
                                                            : 'Show'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </>
            )}

            <PolarisConfirmModal
                open={pendingMove != null}
                title="Move sport to another category?"
                message={
                    pendingMove ? (
                        <>
                            Move <strong>{pendingMove.sportName}</strong> from{' '}
                            <strong>{pendingMove.fromCategory}</strong> to{' '}
                            <strong>{pendingMove.toCategory}</strong>?
                            <br />
                            <br />
                            This only changes picker grouping. Existing athlete selections and the sport name stay the same.
                        </>
                    ) : null
                }
                confirmLabel="Move sport"
                loadingLabel="Moving…"
                loading={moving}
                error={modalError}
                onConfirm={handleConfirmMove}
                onClose={handleCloseMoveModal}
            />
        </div>
    );
}
